import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import GetAmbulanceCard from "../components/GetAmbulancecard";
import ChooseAmbulance from "../components/ChooseAmbulancecard";
import MapBackground from "../components/mapCompoent";
import SuccessModal from "../components/ui/SuccessModal";
import ErrorModal from "../components/ui/ErrorModal";
import OpenToOtherFleetsModal from "../components/ambulance/OpenToOtherFleetsModal";
import AmbulanceBookingConfirmationModal from "../components/ambulance/AmbulanceBookingConfirmationModal";
import PaymentInitiationModal from "../components/payment/modals/PaymentInitiationModal";
import PaymentVerificationModal from "../components/payment/modals/PaymentVerificationModal";
import PaymentOrderCreationModal from "../components/payment/modals/PaymentOrderCreationModal";
import NotificationContainer from "../components/ui/NotificationContainer";
import apiService from "../services/apiService";
import hospitalService from "../services/hospitalService";
import { bookingService, authService } from "../services";
import { useAuth } from "../contexts/AuthContext";
import useNotifications from "../hooks/useNotifications";

/**
 * Reusable AmbulanceInstantBooking Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.showMapBackground - Show/hide map background (default: true)
 * @param {boolean} props.showGetAmbulanceCard - Show/hide GetAmbulanceCard (default: true)
 * @param {boolean} props.showChooseAmbulance - Show/hide ChooseAmbulance (default: true)
 * @param {Function} props.onBookingChange - Callback for booking changes (default: console.log)
 * @param {string} props.className - Additional CSS classes for container
 * @param {string} props.contentClassName - Additional CSS classes for content area
 * @param {Object} props.mapCenter - Map center coordinates (default: Ahmedabad)
 * @param {number} props.mapZoom - Map zoom level (default: 12)
 * @param {string} props.layout - Layout type: "default", "compact", "full-width" (default: "default")
 */
const AmbulanceInstantBooking = ({
  showMapBackground = true,
  showGetAmbulanceCard = true,
  showChooseAmbulance = true,
  onBookingChange = (data) => console.log(data),
  onTrackBookingClick, // Callback for track booking button
  className = "",
  contentClassName = "",
  mapCenter = { lat: 23.0225, lng: 72.5714 },
  mapZoom = 12,
  layout = "default", // "default", "compact", "full-width"
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Notification system
  const { notification, showError, showSuccess } = useNotifications();

  // State for selected ambulance
  const [selectedAmbulance, setSelectedAmbulance] = useState(null);

  // Function to trigger booking when button is clicked
  const handleBookingButtonClick = () => {
    console.log("📍 Book Ambulance button clicked!");
    if (!selectedAmbulance) {
      setError("Please select an ambulance first");
      return;
    }
    if (!bookingFormData) {
      setError("Booking form data is missing");
      return;
    }
    // Trigger the confirmation
    handleConfirmSelection(selectedAmbulance, bookingFormData);
  };

  // State for location coordinates
  const [pickupCoordinates, setPickupCoordinates] = useState(null);
  const [dropCoordinates, setDropCoordinates] = useState(null);

  // State for fare estimates
  const [fareEstimates, setFareEstimates] = useState({});

  // State to control ChooseAmbulance card visibility
  const [showChooseAmbulanceCard, setShowChooseAmbulanceCard] = useState(false);

  // State for booking form data
  const [bookingFormData, setBookingFormData] = useState(null);

  // State for modals
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    data: null,
  });

  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    data: null,
  });

  // Open-to-others decision modal
  const [openOthersModal, setOpenOthersModal] = useState({
    isOpen: false,
    preferredFleetName: null,
    context: null,
  });

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    bookingId: null,
    bookingData: null,
    estimatedFare: null,
    patientName: null,
    pickupAddress: null,
    destinationAddress: null,
    ambulanceType: null,
  });

  // Payment order creation modal state (for fleet users)
  const [paymentOrderModal, setPaymentOrderModal] = useState({
    isOpen: false,
    bookingId: null,
    bookingData: null,
    estimatedFare: null,
  });

  // Payment verification modal state
  const [verificationModal, setVerificationModal] = useState({
    isOpen: false,
    orderId: null,
    paymentId: null,
    signature: null,
    bookingId: null,
  });

  // Error modal state for validation errors
  const [validationErrorModal, setValidationErrorModal] = useState({
    isOpen: false,
    title: "Validation Error",
    message: "",
  });

  // State for loading and error
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showLoadingModal, setShowLoadingModal] = useState(false);

  // Monitor when both pickup and dropoff coordinates are available
  useEffect(() => {
    if (pickupCoordinates && dropCoordinates) {
      setShowChooseAmbulanceCard(true);
    } else {
      setShowChooseAmbulanceCard(false);
    }
  }, [pickupCoordinates, dropCoordinates]);

  // Handle form data from GetAmbulanceCard
  const handleFormData = (formData) => {
    console.log("📝 Form data received in parent:", {
      hasPhone: !!formData.phone,
      hasEmail: !!formData.email,
      phone: formData.phone || "(empty)",
      email: formData.email || "(empty)",
      fullFormData: formData
    });
    setBookingFormData(formData);
  };

  // Handle ambulance selection
  const handleAmbulanceSelect = (ambulance) => {
    console.log("=== AMBULANCE SELECTION IN PARENT ===");
    console.log("Received ambulance:", ambulance);
    setSelectedAmbulance(ambulance);
    onBookingChange({ selectedAmbulance: ambulance });
  };

  // Handle confirm selection - show confirmation modal
  const handleConfirmSelection = (ambulance, formData = null) => {
    // Use passed formData or fallback to state
    const currentFormData = formData || bookingFormData;

    if (!currentFormData) {
      setError("Please fill in the booking form first");
      return;
    }

    if (!ambulance) {
      setError("Please select an ambulance first");
      return;
    }

    // Update bookingFormData state with the latest formData (including paymentType from ChooseAmbulance card)
    if (formData) {
      setBookingFormData(formData);
    }

    // Set the selected ambulance in state so it's available in handleConfirmBooking
    setSelectedAmbulance(ambulance);

    // Get estimated fare from multiple sources in priority order
    let estimatedFare = null;
    
    // Priority 1: Use estimated_fare directly from ambulance object (from Estimate Fare API)
    if (ambulance?.estimated_fare !== null && ambulance?.estimated_fare !== undefined) {
      estimatedFare = ambulance.estimated_fare;
      console.log("💰 [Confirmation Modal] Using fare from ambulance object:", estimatedFare);
    } 
    // Priority 2: For hospital users, try unique key first
    else if (isHospitalUser() && ambulance?.unique_key && fareEstimates[ambulance.unique_key]) {
      estimatedFare = fareEstimates[ambulance.unique_key];
      console.log("💰 [Confirmation Modal] Using fare from unique key:", estimatedFare);
    }
    // Priority 3: Fall back to type_id
    else if (ambulance?.type_id && fareEstimates[ambulance.type_id]) {
      estimatedFare = fareEstimates[ambulance.type_id];
      console.log("💰 [Confirmation Modal] Using fare from type_id:", estimatedFare);
    } else {
      console.warn("⚠️ [Confirmation Modal] No fare estimate found for ambulance:", ambulance);
    }

    // Prepare confirmation modal data
    const confirmationData = {
      patientName: currentFormData.name,
      phoneNumber: currentFormData.phone,
      pickupAddress: currentFormData.pickupLocation,
      destinationAddress: currentFormData.dropLocation,
      ambulanceType: ambulance?.name || "Ambulance",
      estimatedFare: estimatedFare,
      paymentType: currentFormData.paymentType,
      bookingType: currentFormData.bookingType,
      scheduledDateTime:
        currentFormData.bookingType === "scheduled" &&
        currentFormData.date &&
        currentFormData.time
          ? new Date(
              `${currentFormData.date}T${currentFormData.time}`
            ).toISOString()
          : undefined,
      direction: currentFormData.direction,
      pickupCoordinates: pickupCoordinates,
      dropCoordinates: dropCoordinates,
    };

    setConfirmationModal({
      isOpen: true,
      data: confirmationData,
    });
  };

  // Check for payment redirect on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('paymentStatus');
    const bookingId = urlParams.get('bookingId');
    const paymentId = urlParams.get('paymentId');

    if (paymentStatus === 'success' && bookingId) {
      // Payment was successful, verify payment status first, then fetch full booking details
      const fetchBookingDetails = async () => {
        try {
          console.log('Verifying payment status for booking:', bookingId);
          
          // First, verify the booking exists and payment status is PAID
          let bookingResponse;
          if (isFleetUser()) {
            console.log("🚛 Using fleet v1 booking details API");
            bookingResponse = await authService.fleetGetBookingDetails(bookingId);
          } else {
            console.log("👤 Using general booking details API");
            bookingResponse = await bookingService.getBookingById(bookingId);
          }
          
          if (!bookingResponse.success || !bookingResponse.data) {
            setError('Booking not found. Payment may not have been processed correctly.');
            return;
          }
          
          const booking = bookingResponse.data;
          
          // Check if payment status is actually PAID
          const actualPaymentStatus = booking.payment_status || booking.paymentStatus;
          if (actualPaymentStatus !== 'PAID') {
            setError(`Payment verification failed. Payment status: ${actualPaymentStatus}. Please contact support.`);
            return;
          }
          
          console.log('Payment verified as PAID. Fetching full booking details...');
          
          // Extract driver information using the same helper logic
          const assignedDriver = booking.driver_matching?.assigned_driver || booking.driver || booking.vehicle?.assigned_driver;
          const vehicle = booking.vehicle;
          const operator = assignedDriver?.operator || vehicle?.operator || booking.operator;
          const driverMatching = booking.driver_matching || {};
          const driverStatus = driverMatching?.success 
            ? driverMatching?.message || 'Driver assigned'
            : 'Waiting for driver assignment...';
          const matchingStatus = driverMatching?.success ? 'assigned' : 'searching';
          const driverName = assignedDriver?.driver_name || assignedDriver?.name || 'N/A';
          const driverPhone = assignedDriver?.phone || assignedDriver?.contact_phone || assignedDriver?.phone_number || 'N/A';
          const vendorName = operator?.name || operator?.operator_name || vehicle?.operator_name || 'N/A';
          const vendorContact = operator?.contact_info || operator?.phone || operator?.contact_phone || 'N/A';
          const vehicleNumber = vehicle?.vehicle_number || vehicle?.registration_number || vehicle?.ambulance_number || 'N/A';

          // Extract fare information
          const estimatedFare = booking.estimated_fare || booking.estimatedFare || booking.final_fare || booking.finalFare;
          const finalFare = booking.final_fare || booking.finalFare || estimatedFare;

          // Extract addresses - use direct fields first (like cash payment format)
          const pickupAddress = booking.pickup_address || booking.pickupAddress || booking.pickup_location?.address || booking.pickupLocation?.address || 'N/A';
          const destinationAddress = booking.dropoff_address || booking.dropoffAddress || booking.destination_address || booking.destinationAddress || booking.dropoff_location?.address || booking.dropoffLocation?.address || 'N/A';

          // Show success modal with same format as cash payment, just add payment confirmation
          setSuccessModal({
            isOpen: true,
            data: {
              bookingId: bookingId,
              patientName: booking.patient_name || booking.patientName || 'N/A',
              hospitalName: "Selected Hospital", // Same as cash payment
              ambulanceType: booking.vehicle?.type?.name || booking.vehicle?.name || booking.ambulanceType || "Ambulance",
              estimatedFare: finalFare, // Use finalFare like cash payment
              finalFare: finalFare,
              driverStatus: driverStatus,
              driverName: driverName,
              driverPhone: driverPhone,
              vendorName: vendorName,
              vendorContact: vendorContact,
              vehicleNumber: vehicleNumber,
              pickupAddress: pickupAddress, // Direct string like cash payment
              destinationAddress: destinationAddress, // Direct string like cash payment
              bookingType: booking.booking_type || booking.bookingType,
              direction: booking.journey_direction || booking.direction || booking.booking_direction,
              matchingStatus: matchingStatus,
              // Add payment confirmation
              paymentStatus: 'success',
              paymentId: paymentId
            }
          });

          console.log('Booking details loaded:', booking);
        } catch (error) {
          console.error('Error fetching booking details after payment:', error);
          // Fallback if API call fails
          setSuccessModal({
            isOpen: true,
            data: {
              bookingId: bookingId,
              paymentStatus: 'success',
              paymentId: paymentId,
              message: 'Payment completed successfully! Your booking is confirmed. (Unable to load full booking details)'
            }
          });
        }
      };

      fetchBookingDetails();

      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Helper: Validate mobile number is complete
  const validateMobileNumber = (phone, isFleetUser = false, isHospitalUser = false) => {
    if (!phone || !phone.trim()) {
      return { isValid: false, message: "Mobile number is required. Please enter a complete 10-digit mobile number." };
    }

    const phoneDigits = phone.replace(/\D/g, ""); // Remove all non-digits

    // For fleet dashboard or hospital dashboard, allow 1-10 digits (flexible international format)
    // But if a number is started, it should be at least 10 digits to be considered complete
    if (isFleetUser || isHospitalUser) {
      if (phoneDigits.length > 0 && phoneDigits.length < 10) {
        return { isValid: false, message: "Mobile number is incomplete. Please enter a complete 10-digit mobile number." };
      }
      if (phoneDigits.length > 10) {
        return { isValid: false, message: "Mobile number is too long. Please enter a valid 10-digit mobile number." };
      }
      // For hospital/fleet, at least 10 digits is required if phone is provided
      if (phoneDigits.length < 10) {
        return { isValid: false, message: "Mobile number is incomplete. Please enter a complete 10-digit mobile number." };
      }
    } else {
      // For regular users, validate Indian phone number format (exactly 10 digits starting with 6-9)
      if (phoneDigits.length !== 10) {
        return { isValid: false, message: "Mobile number must be exactly 10 digits. Please enter a complete mobile number." };
      }
      if (!/^[6-9]\d{9}$/.test(phoneDigits)) {
        return { isValid: false, message: "Invalid mobile number. Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9." };
      }
    }

    return { isValid: true, message: "" };
  };

  // Helper: Validate booking prerequisites
  const validateBookingPrerequisites = () => {
    if (!selectedAmbulance) {
      setValidationErrorModal({
        isOpen: true,
        title: "Validation Error",
        message: "Please select an ambulance before proceeding with the booking.",
      });
      throw new Error("Please select an ambulance first");
    }

    if (!bookingFormData) {
      setValidationErrorModal({
        isOpen: true,
        title: "Validation Error",
        message: "Booking form data is missing. Please fill in all required information.",
      });
      throw new Error("Booking form data is missing");
    }

    // Validate patient name
    if (!bookingFormData.name || !bookingFormData.name.trim()) {
      setValidationErrorModal({
        isOpen: true,
        title: "Validation Error",
        message: "Patient name is required. Please enter the patient's name.",
      });
      throw new Error("Patient name is required");
    }

    // Validate locations
    if (!pickupCoordinates || !dropCoordinates) {
      setValidationErrorModal({
        isOpen: true,
        title: "Validation Error",
        message: "Both pickup and drop locations are required. Please select both locations on the map.",
      });
      throw new Error("Pickup and drop locations are required");
    }

    // Validate mobile number - if phone is provided, it must be complete
    // If no phone, email must be provided
    const hasPhone = bookingFormData.phone && bookingFormData.phone.trim();
    const hasEmail = bookingFormData.email && bookingFormData.email.trim();

    if (!hasPhone && !hasEmail) {
      setValidationErrorModal({
        isOpen: true,
        title: "Validation Error",
        message: "Either mobile number or email is required. Please provide at least one contact method.",
      });
      throw new Error("Contact information (mobile number or email) is required");
    }

    // If phone is provided, validate it's complete
    if (hasPhone) {
      const phoneValidation = validateMobileNumber(
        bookingFormData.phone,
        isFleetUser(),
        isHospitalUser()
      );
      if (!phoneValidation.isValid) {
        setValidationErrorModal({
          isOpen: true,
          title: "Incomplete Mobile Number",
          message: phoneValidation.message,
        });
        throw new Error(phoneValidation.message);
      }
    }

    // Validate scheduled booking date and time
    if (bookingFormData.bookingType === "scheduled") {
      if (!bookingFormData.date || !bookingFormData.date.trim()) {
        setValidationErrorModal({
          isOpen: true,
          title: "Validation Error",
          message: "Date is required for scheduled bookings. Please select a date.",
        });
        throw new Error("Date is required for scheduled bookings");
      }
      if (!bookingFormData.time || !bookingFormData.time.trim()) {
        setValidationErrorModal({
          isOpen: true,
          title: "Validation Error",
          message: "Time is required for scheduled bookings. Please select a time.",
        });
        throw new Error("Time is required for scheduled bookings");
      }
    }
  };

  // Helper: Check if user is a hospital user
  const isHospitalUser = () => {
    return user?.role === 'HOSPITAL_USER' ||
           user?.role === 'hospital' ||
           user?.role === 'Hospital';
  };

  // Helper: Check if user is a fleet user
  const isFleetUser = () => {
    return user?.role === 'TENANT_ADMIN' ||
           user?.role === 'TENANT_USER' ||
           user?.role === 'fleet_operator' ||
           user?.role === 'Fleet';
  };

  // Helper: Get hospital ID from user profile
  const getHospitalIdFromUser = () => {
    return user?.hospital_id || 
           user?.hospitalId || 
           user?.hospital?.hospital_id ||
           user?.hospitals?.[0]?.hospital_id;
  };

  // Helper: Determine payment method
  const determinePaymentMethod = (paymentType) => {
    if (paymentType === "Online") {
      return "ONLINE";
    }
    if (paymentType === "Cash") {
      return "CASH";
    }
    return "PAY_LATER";
  };

  // Helper: Get backend fare estimate only
  const calculateEstimatedFare = (ambulance, fareEstimates) => {
    // For hospital users, check multiple sources for the fare
    if (isHospitalUser()) {
      // Priority 1: Use estimated_fare directly from ambulance object (from Estimate Fare API)
      if (ambulance?.estimated_fare !== null && ambulance?.estimated_fare !== undefined) {
        console.log("💰 Using fare from ambulance object:", ambulance.estimated_fare);
        return ambulance.estimated_fare;
      }
      
      // Priority 2: Use unique key (type_id_operator_id) for hospital users
      if (ambulance?.unique_key && fareEstimates[ambulance.unique_key]) {
        console.log("💰 Using fare from unique key:", fareEstimates[ambulance.unique_key]);
        return fareEstimates[ambulance.unique_key];
      }
    }
    
    // Priority 3: Use type_id (for non-hospital users or fallback)
    const fareFromState = ambulance?.type_id && fareEstimates[ambulance.type_id];
    if (fareFromState) {
      console.log("💰 Using fare from type_id:", fareFromState);
      return fareFromState;
    }
    
    return null; // Return null if no backend estimate available
  };

  // Helper: Build scheduled date time
  const buildScheduledDateTime = (bookingType, date, time) => {
    if (bookingType === "scheduled" && date && time) {
      return new Date(`${date}T${time}`).toISOString();
    }
    return undefined;
  };

  // Helper: Build booking data object
  const buildBookingData = (hospitalId, bookingFormData, selectedAmbulance, pickupCoordinates, dropCoordinates, fareEstimates) => {
    const calculatedFare = calculateEstimatedFare(selectedAmbulance, fareEstimates);
    
    const bookingData = {
      ...(hospitalId && user?.role !== "TENANT_ADMIN" && { hospitalId: hospitalId }),
      patientName: bookingFormData.name,
      patientAge: 30,
      patientGender: "OTHER",
      pickupLocation: {
        address: bookingFormData.pickupLocation,
        latitude: pickupCoordinates.latitude,
        longitude: pickupCoordinates.longitude,
      },
      dropoffLocation: {
        address: bookingFormData.dropLocation,
        latitude: dropCoordinates.latitude,
        longitude: dropCoordinates.longitude,
      },
      emergencyType: "NON_URGENT",
      priority: "NORMAL",
      bookingType: bookingFormData.bookingType === "scheduled" ? "SCHEDULED" : "INSTANT",
      scheduledDateTime: buildScheduledDateTime(
        bookingFormData.bookingType,
        bookingFormData.date,
        bookingFormData.time
      ),
      notes: `Journey Direction: ${bookingFormData.direction}.`,
      ambulanceType: selectedAmbulance.type_id,
      journeyDirection: bookingFormData.direction.toUpperCase(),
      paymentMethod: determinePaymentMethod(bookingFormData.paymentType),
    };

    if (bookingFormData?.fleetPartnerIds?.length > 0) {
      bookingData.fleetPartnerIds = bookingFormData.fleetPartnerIds;
    }
    if (bookingFormData?.preferredFleetId) {
      bookingData.preferredFleetId = bookingFormData.preferredFleetId;
    }
    // Add selected hospital ID for fleet users (from fetch hospitals API)
    if (isFleetUser() && bookingFormData?.hospitalPartnerId) {
      bookingData.hospitalId = bookingFormData.hospitalPartnerId;
    }
    if (calculatedFare && calculatedFare > 0) {
      bookingData.estimatedFare = calculatedFare;
    }

    return bookingData;
  };

  // Helper: Add contact information to booking data
  const addContactInfo = (bookingData, bookingFormData) => {
    if (bookingFormData.phone?.trim()) {
      bookingData.patientPhone = bookingFormData.phone.trim();
      bookingData.patientPhone_country_code = "+91";
    }
    if (bookingFormData.email?.trim()) {
      bookingData.patientEmail = bookingFormData.email.trim();
    }
  };

  // Helper: Extract booking ID from response
  const extractBookingId = (result) => {
    return result?.data?.booking_id ||
           result?.data?.id ||
           result?.booking_id ||
           result?.id ||
           null;
  };



  // Helper: Extract driver matching info
  const extractDriverInfo = (result) => {
    const driverMatching = result?.data?.driver_matching;
    const assignedDriver = driverMatching?.assigned_driver || result?.data?.driver || result?.data?.vehicle?.assigned_driver;
    const vehicle = result?.data?.vehicle;
    const operator = assignedDriver?.operator || vehicle?.operator || result?.data?.operator;
    
    // Extract driver phone from multiple possible locations
    const driverPhone = assignedDriver?.phone || 
                       assignedDriver?.contact_phone || 
                       assignedDriver?.phone_number ||
                       assignedDriver?.mobile ||
                       result?.data?.driver?.phone ||
                       result?.data?.driver?.contact_phone ||
                       result?.data?.driver?.phone_number ||
                       result?.data?.driver?.mobile ||
                       driverMatching?.assigned_driver?.phone ||
                       driverMatching?.assigned_driver?.contact_phone ||
                       driverMatching?.assigned_driver?.phone_number ||
                       '';
    
    return {
      driverMatching,
      driverStatus: driverMatching?.success 
        ? driverMatching?.message || 'Driver assigned'
        : 'Waiting for driver assignment...',
      matchingStatus: driverMatching?.success ? 'assigned' : 'searching',
      driverName: assignedDriver?.driver_name || assignedDriver?.name || driverMatching?.assigned_driver?.driver_name || result?.data?.driver?.name,
      driverPhone: driverPhone,
      driverId: assignedDriver?.driver_id || assignedDriver?.id || result?.data?.driver?.driver_id,
      vendorName: operator?.name || 
                 operator?.operator_name || 
                 operator?.fleet_name ||
                 operator?.company_name ||
                 vehicle?.operator_name || 
                 vehicle?.operator?.name ||
                 vehicle?.fleet_name ||
                 result?.data?.operator?.name ||
                 result?.data?.operator?.operator_name ||
                 result?.data?.fleet?.name ||
                 result?.data?.fleet_name ||
                 '',
      vendorContact: (() => {
        const contact = operator?.contact_info || 
                      operator?.phone || 
                      operator?.contact_phone ||
                      operator?.mobile ||
                      operator?.mobile_number ||
                      operator?.contact_number ||
                      operator?.fleet?.phone ||
                      operator?.fleet?.contact_phone ||
                      vehicle?.operator?.phone ||
                      vehicle?.operator?.contact_phone ||
                      vehicle?.operator?.contact_info ||
                      vehicle?.fleet?.phone ||
                      vehicle?.fleet?.contact_phone ||
                      result?.data?.operator?.contact_info ||
                      result?.data?.operator?.phone ||
                      result?.data?.operator?.contact_phone ||
                      result?.data?.operator?.mobile ||
                      result?.data?.fleet?.phone ||
                      result?.data?.fleet?.contact_phone ||
                      result?.data?.fleet?.mobile ||
                      result?.data?.vehicle?.operator?.phone ||
                      result?.data?.vehicle?.operator?.contact_phone ||
                      result?.data?.vehicle?.fleet?.phone ||
                      '';
        
        console.log('📞 [AmbulanceInstantBooking] Vendor contact extracted:', {
          contact,
          operator: operator,
          operatorPhone: operator?.phone,
          operatorContactInfo: operator?.contact_info,
          operatorContactPhone: operator?.contact_phone,
          vehicle: vehicle,
          vehicleOperator: vehicle?.operator,
          resultDataOperator: result?.data?.operator,
          resultDataFleet: result?.data?.fleet,
          fullResult: result?.data
        });
        
        return contact;
      })(),
      vehicleNumber: vehicle?.vehicle_number || vehicle?.registration_number || vehicle?.ambulance_number || result?.data?.vehicle?.vehicle_number,
    };
  };

  // Helper: Build success modal data
  const buildSuccessModalData = (bookingId, bookingFormData, selectedAmbulance, finalFare, driverInfo) => {
    // Build scheduled date time for display
    let scheduledDateTimeForDisplay = undefined;
    if (bookingFormData.bookingType === "scheduled" && bookingFormData.date && bookingFormData.time) {
      scheduledDateTimeForDisplay = new Date(`${bookingFormData.date}T${bookingFormData.time}`).toISOString();
    }
    
    const modalData = {
      bookingId: bookingId,
      patientName: bookingFormData.name,
      hospitalName: "Selected Hospital",
      ambulanceType: selectedAmbulance?.name || "Ambulance",
      estimatedFare: finalFare,
      finalFare: finalFare,
      driverStatus: driverInfo.driverStatus,
      driverName: driverInfo.driverName,
      driverPhone: driverInfo.driverPhone,
      driverId: driverInfo.driverId,
      vendorName: driverInfo.vendorName,
      vendorContact: driverInfo.vendorContact,
      vehicleNumber: driverInfo.vehicleNumber,
      pickupAddress: bookingFormData.pickupLocation,
      destinationAddress: bookingFormData.dropLocation,
      bookingType: bookingFormData.bookingType === "scheduled" ? "SCHEDULED" : (bookingFormData.bookingType || "INSTANT"),
      direction: bookingFormData.direction,
      matchingStatus: driverInfo.matchingStatus,
      scheduledDateTime: scheduledDateTimeForDisplay,
      scheduledDate: bookingFormData.date,
      scheduledTime: bookingFormData.time,
    };
    
    // Debug: Log what's being passed to success modal
    console.log('📋 [AmbulanceInstantBooking] Building success modal data:', {
      vendorName: modalData.vendorName,
      vendorContact: modalData.vendorContact,
      driverInfo: driverInfo,
      bookingType: modalData.bookingType,
      scheduledDateTime: modalData.scheduledDateTime,
      fullModalData: modalData
    });
    
    return modalData;
  };

  // Helper: Check if should show open others modal
  const shouldShowOpenOthersModal = (bookingFormData, driverMatching) => {
    const hadPreferred = !!bookingFormData?.preferredFleetId;
    const hadFleetList = Array.isArray(bookingFormData?.fleetPartnerIds) && 
                         bookingFormData.fleetPartnerIds.length > 0;
    return hadPreferred && hadFleetList && !driverMatching?.success;
  };

  // Helper: Process successful booking
  const processSuccessfulBooking = (bookingId, bookingData, result, bookingFormData, selectedAmbulance, fareEstimates) => {
    // Try to get fare from multiple sources in priority order
    let finalFare = 0;
    
    // Priority 1: Get from booking API response
    if (result?.data?.estimated_fare !== null && result?.data?.estimated_fare !== undefined) {
      finalFare = result.data.estimated_fare;
      console.log("💰 Using fare from booking API response:", finalFare);
    } else if (result?.data?.estimatedFare !== null && result?.data?.estimatedFare !== undefined) {
      finalFare = result.data.estimatedFare;
      console.log("💰 Using fare from booking API response (camelCase):", finalFare);
    } else if (result?.estimated_fare !== null && result?.estimated_fare !== undefined) {
      finalFare = result.estimated_fare;
      console.log("💰 Using fare from booking API response (root):", finalFare);
    } else {
      // Priority 2: Calculate from selected ambulance and fare estimates
      finalFare = calculateEstimatedFare(selectedAmbulance, fareEstimates) || 0;
      if (finalFare > 0) {
        console.log("💰 Using calculated fare:", finalFare);
      } else {
        console.warn("⚠️ No fare found, using 0 as fallback");
      }
    }
    
    const driverInfo = extractDriverInfo(result);

    // Debug: Log payment type to verify it's being passed correctly
    console.log('💳 [AmbulanceInstantBooking] Processing successful booking:', {
      bookingId,
      paymentType: bookingFormData.paymentType,
      isOnlinePayment: bookingFormData.paymentType === "Online",
      isFleetUser: isFleetUser(),
      isHospitalUser: isHospitalUser()
    });

    // Check if payment type is "Online" - if so, open payment modal instead of success modal
    if (bookingFormData.paymentType === "Online") {
      // For fleet users, open PaymentOrderCreationModal directly
      // For hospital users, open PaymentInitiationModal
      if (isFleetUser()) {
        console.log('🚛 [AmbulanceInstantBooking] Fleet user detected - opening PaymentOrderCreationModal');
        setPaymentOrderModal({
          isOpen: true,
          bookingId: bookingId,
          bookingData: {
            estimated_fare: finalFare,
            estimatedFare: finalFare,
            final_fare: finalFare,
            finalFare: finalFare,
            fare: finalFare,
            amount: finalFare,
          },
          estimatedFare: finalFare,
        });

        // Store booking data for later use after payment success
        setSuccessModal({
          isOpen: false,
          data: {
            ...buildSuccessModalData(bookingId, bookingFormData, selectedAmbulance, finalFare, driverInfo),
            pendingPayment: true,
          }
        });
      } else {
        // Hospital users - use PaymentInitiationModal
        console.log('🏥 [AmbulanceInstantBooking] Hospital user detected - opening PaymentInitiationModal');
        setPaymentModal({
          isOpen: true,
          bookingId: bookingId,
          bookingData: {
            patientPhone: bookingFormData.phone,
            patientEmail: bookingFormData.email,
          },
          estimatedFare: finalFare,
          patientName: bookingFormData.name,
          pickupAddress: bookingFormData.pickupLocation,
          destinationAddress: bookingFormData.dropLocation,
          ambulanceType: selectedAmbulance?.name || "Ambulance",
        });

        // Store booking data for later use after payment success
        setSuccessModal({
          isOpen: false,
          data: {
            ...buildSuccessModalData(bookingId, bookingFormData, selectedAmbulance, finalFare, driverInfo),
            pendingPayment: true,
          }
        });
      }
    } else {
      // For cash or other payment types, show success modal directly
      setSuccessModal({
        isOpen: true,
        data: buildSuccessModalData(bookingId, bookingFormData, selectedAmbulance, finalFare, driverInfo)
      });

      // Auto-redirect to tracking screen after 2.5s so user sees confirmation toast and modal first
      if (bookingId) {
        setTimeout(() => {
          handleTrackBooking(bookingId);
        }, 2500);
      }
    }

    if (shouldShowOpenOthersModal(bookingFormData, driverInfo.driverMatching)) {
      setOpenOthersModal({
        isOpen: true,
        preferredFleetName: null,
        context: { bookingId, fleetPartnerIds: bookingFormData.fleetPartnerIds },
      });
    }

    onBookingChange({
      bookingId,
      ...bookingData,
      success: true,
      result,
    });
  };

  // Handle actual booking creation
  const handleConfirmBooking = async () => {
    setIsLoading(true);
    setConfirmationModal({ isOpen: false, data: null });
    setShowLoadingModal(true);

    try {
      validateBookingPrerequisites();

      console.log("🔍 About to prepare booking data. Current bookingFormData:", {
        phone: bookingFormData.phone,
        email: bookingFormData.email,
        name: bookingFormData.name,
        hasPhone: !!bookingFormData.phone,
        hasEmail: !!bookingFormData.email
      });

      const hospitalId = getHospitalIdFromUser();
      let bookingData = buildBookingData(
        hospitalId,
        bookingFormData,
        selectedAmbulance,
        pickupCoordinates,
        dropCoordinates,
        fareEstimates
      );

      addContactInfo(bookingData, bookingFormData);

      console.log("🏥 Hospital ID from user profile:", hospitalId);
      console.log("🏥 User object:", user);
      console.log("💰 Estimated fare being sent:", bookingData.estimatedFare);
      console.log("💰 Selected ambulance:", selectedAmbulance);
      console.log("📦 Creating booking with data:", bookingData);

      // Use appropriate endpoint based on user type
      let result;
      if (isHospitalUser()) {
        console.log("🏥 Using hospital v1 booking API");
        result = await hospitalService.createBooking(bookingData);
      } else if (isFleetUser()) {
        console.log("🚛 Using fleet v1 booking API");
        result = await authService.fleetCreateBooking(bookingData);
      } else {
        console.log("👤 Using general booking API");
        result = await apiService.createBooking(bookingData);
      }
      
      console.log("📦 Booking creation response:", result);
      
      const bookingId = extractBookingId(result);

      if (!bookingId) {
        throw new Error("No booking ID received from server");
      }

      // Show confirmation message / toast
      showSuccess(
        "Booking Confirmed",
        "Booking confirmed successfully"
      );

      processSuccessfulBooking(
        bookingId,
        bookingData,
        result,
        bookingFormData,
        selectedAmbulance,
        fareEstimates
      );

      console.log("Booking created successfully. Smart matching will be handled by backend.");
    } catch (error) {
      console.error("Booking creation failed:", error);
      
      // If validation error modal is already shown, don't show regular error
      if (validationErrorModal.isOpen) {
        // Validation error modal is already displayed, just reset loading state
        setIsLoading(false);
        setShowLoadingModal(false);
        return;
      }
      
      // Extract error message from various possible response structures
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          "Failed to create booking. Please try again.";
      
      // Check if it's the specific "no ambulances available" error
      const isAmbulanceUnavailableError = errorMessage.toLowerCase().includes("no ambulances are currently available") ||
                                         errorMessage.toLowerCase().includes("no ambulances available");
      
      if (isAmbulanceUnavailableError) {
        // Show notification that auto-closes after 5 seconds
        showError(
          'No Ambulances Available',
          errorMessage
        );
        // Also clear the error state after showing notification
        setError("");
      } else {
        // For other errors, use the existing error display
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
      setShowLoadingModal(false);
    }
  };

  // Handle track booking navigation
  const handleTrackBooking = (customBookingId) => {
    const bookingId = customBookingId || successModal.data?.bookingId;
    if (!bookingId) return;

    // Close modal
    setSuccessModal({ isOpen: false, data: null });

    // Use callback if provided (from dashboard), otherwise navigate
    if (onTrackBookingClick) {
      onTrackBookingClick(bookingId);
    } else {
      navigate(`/track-booking?bookingId=${bookingId}`);
    }
  };

  // Handle success modal close
  const handleSuccessModalClose = () => {
    const bookingId = successModal.data?.bookingId;
    setSuccessModal({ isOpen: false, data: null });
    // Reset form
    setBookingFormData(null);
    setSelectedAmbulance(null);
    setPickupCoordinates(null);
    setDropCoordinates(null);
    setShowChooseAmbulanceCard(false);

    // Redirect to tracking screen on modal close if bookingId exists
    if (bookingId) {
      if (onTrackBookingClick) {
        onTrackBookingClick(bookingId);
      } else {
        navigate(`/track-booking?bookingId=${bookingId}`);
      }
    }
  };

  // Handle open-to-others confirmation
  const handleConfirmOpenToOthers = async () => {
    const { context } = openOthersModal;
    setOpenOthersModal({ isOpen: false, preferredFleetName: null, context: null });
    try {
      if (context?.bookingId && context?.fleetPartnerIds?.length > 0) {
        await apiService.post(`/bookings/${context.bookingId}/open-to-others`, {
          fleetPartnerIds: context.fleetPartnerIds,
        });
      }
    } catch (e) {
      console.warn('Failed to open booking to other fleets:', e);
    }
  };

  const handleCancelOpenToOthers = () => {
    setOpenOthersModal({ isOpen: false, preferredFleetName: null, context: null });
  };

  // Handle payment success - opens verification modal
  const handlePaymentSuccess = (paymentResponse) => {
    console.log('✅ [AmbulanceInstantBooking] Payment successful, opening verification modal:', paymentResponse);
    
    // Store bookingId before clearing payment modal state
    const bookingId = paymentModal.bookingId;

    // Close payment initiation modal
    setPaymentModal({
      isOpen: false,
      bookingId: null,
      bookingData: null,
      estimatedFare: null,
      patientName: null,
      pickupAddress: null,
      destinationAddress: null,
      ambulanceType: null,
    });

    // Open payment verification modal with payment details
    setVerificationModal({
      isOpen: true,
      orderId: paymentResponse.orderId,
      paymentId: paymentResponse.paymentId,
      signature: paymentResponse.signature,
      bookingId: bookingId,
    });
  };

  // Handle payment verification success
  const handleVerificationSuccess = (verificationData) => {
    console.log('✅ [AmbulanceInstantBooking] Payment verified successfully:', verificationData);
    
    // Close verification modal
    setVerificationModal({
      isOpen: false,
      orderId: null,
      paymentId: null,
      signature: null,
      bookingId: null,
    });

    // Show success modal with payment confirmation
    if (successModal.data) {
      setSuccessModal({
        isOpen: true,
        data: {
          ...successModal.data,
          paymentStatus: 'success',
          paymentId: verificationData.paymentId || verificationModal.paymentId,
          orderId: verificationData.orderId || verificationModal.orderId,
          pendingPayment: false,
        }
      });
    }

    // Update URL with payment info
    const bookingId = verificationData.bookingId || verificationModal.bookingId;
    if (bookingId) {
      const url = new URL(window.location.href);
      url.searchParams.set('paymentStatus', 'success');
      url.searchParams.set('bookingId', bookingId);
      if (verificationData.paymentId) {
        url.searchParams.set('paymentId', verificationData.paymentId);
      }
      window.history.replaceState({}, document.title, url.toString());
    }
  };

  // Handle payment verification failure
  const handleVerificationFailure = (error) => {
    console.error('❌ [AmbulanceInstantBooking] Payment verification failed:', error);
    setError(error || 'Payment verification failed. Please contact support.');
  };

  // Handle payment error
  const handlePaymentError = (errorMessage) => {
    console.error('❌ [AmbulanceInstantBooking] Payment error:', errorMessage);
    setError(errorMessage || 'Payment failed. Please try again.');
    // Keep payment modal open so user can retry
  };

  const getLayoutClasses = () => {
    switch (layout) {
      case "compact":
        return "p-2 gap-4";
      case "full-width":
        return "p-6 gap-8";
      default:
        return "p-0 gap-6";
    }
  };

  const getContentLayout = () => {
    switch (layout) {
      case "compact":
        return "flex-col sm:flex-row";
      case "full-width":
        return "flex-row";
      default:
        return "flex-col lg:flex-row";
    }
  };

  const getFormLayout = () => {
    switch (layout) {
      case "compact":
        return "flex-col sm:flex-row";
      case "full-width":
        return "flex-row";
      default:
        return "flex-col xl:flex-row";
    }
  };

  return (
    <div className={`relative min-h-screen w-full ${className}`}>
      {/* Main Content Area */}
      <div
        className={`relative z-10 flex ${getContentLayout()} items-start justify-between ${getLayoutClasses()} min-h-screen ${contentClassName}`}
      >
        {/* Left Side - Booking Forms */}
        {(showGetAmbulanceCard || showChooseAmbulance) && (
          <div className={`flex ${getFormLayout()} gap-6 flex-1 relative z-20`}>
            {showGetAmbulanceCard && (
              <GetAmbulanceCard
                onBookingChange={onBookingChange}
                selectedAmbulance={selectedAmbulance}
                triggerBooking={handleBookingButtonClick}
                onPickupCoordinatesChange={setPickupCoordinates}
                onDropCoordinatesChange={setDropCoordinates}
                fareEstimates={fareEstimates}
                onShowChooseAmbulance={() => setShowChooseAmbulanceCard(true)}
                onFormDataChange={handleFormData}
              />
            )}
            {showChooseAmbulance && showChooseAmbulanceCard && (
              <ChooseAmbulance
                selectedAmbulance={selectedAmbulance}
                pickupCoordinates={pickupCoordinates}
                dropCoordinates={dropCoordinates}
                onAmbulanceSelect={handleAmbulanceSelect}
                onConfirmSelection={handleConfirmSelection}
                onFareEstimatesChange={setFareEstimates}
                journeyDirection={bookingFormData?.direction?.toUpperCase()}
                bookingFormData={bookingFormData}
                triggerBooking={handleBookingButtonClick}
              />
            )}
          </div>
        )}

        {/* Right Side - Map Background + Ambulance List */}
        <div className="relative w-1/2 min-h-screen">
          {/* Map Background - Show only when both locations are selected */}
          {showMapBackground && pickupCoordinates && dropCoordinates && (
            <div className="absolute inset-0 w-full h-full">
              <MapBackground
                center={mapCenter}
                zoom={mapZoom}
                pickupLocation={pickupCoordinates}
                dropoffLocation={dropCoordinates}
              />
            </div>
          )}

          {/* Placeholder when no locations selected */}
          {(!pickupCoordinates || !dropCoordinates) && (
            <div className="absolute inset-0 w-full h-full bg-gray-100 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  Select Pickup & Drop Locations
                </h3>
                <p className="text-sm text-gray-500">
                  Enter both pickup and dropoff locations to see the map and
                  available ambulances
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 z-50 max-w-sm">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-red-700 font-medium">{error}</p>
            <button
              onClick={() => setError("")}
              className="text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <AmbulanceBookingConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() =>
          !isLoading && setConfirmationModal({ isOpen: false, data: null })
        }
        onConfirm={handleConfirmBooking}
        patientName={confirmationModal.data?.patientName}
        phoneNumber={confirmationModal.data?.phoneNumber}
        pickupAddress={confirmationModal.data?.pickupAddress}
        destinationAddress={confirmationModal.data?.destinationAddress}
        ambulanceType={confirmationModal.data?.ambulanceType}
        estimatedFare={confirmationModal.data?.estimatedFare}
        bookingType={confirmationModal.data?.bookingType}
        scheduledDateTime={confirmationModal.data?.scheduledDateTime}
        isLoading={isLoading}
      />

      {/* Loading Modal */}
      {showLoadingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Creating Booking
              </h3>
              <p className="text-gray-600">
                Please wait while we process your ambulance booking...
              </p>
            </div>

            <div className="space-y-3 text-sm text-gray-500">
              <div className="flex items-center justify-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                Processing booking details
              </div>
              <div className="flex items-center justify-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                Assigning nearest driver
              </div>
              <div className="flex items-center justify-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                Calculating final fare
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={handleSuccessModalClose}
        bookingId={successModal.data?.bookingId}
        patientName={successModal.data?.patientName}
        hospitalName={successModal.data?.hospitalName}
        ambulanceType={successModal.data?.ambulanceType}
        estimatedFare={successModal.data?.estimatedFare}
        finalFare={successModal.data?.finalFare}
        driverStatus={successModal.data?.driverStatus}
        driverName={successModal.data?.driverName}
        driverPhone={successModal.data?.driverPhone}
        driverId={successModal.data?.driverId}
        vendorName={successModal.data?.vendorName}
        vendorContact={successModal.data?.vendorContact}
        vehicleNumber={successModal.data?.vehicleNumber}
        pickupAddress={successModal.data?.pickupAddress}
        destinationAddress={successModal.data?.destinationAddress}
        bookingType={successModal.data?.bookingType}
        direction={successModal.data?.direction}
        scheduledDateTime={successModal.data?.scheduledDateTime}
        scheduledDate={successModal.data?.scheduledDate}
        scheduledTime={successModal.data?.scheduledTime}
        onTrackBooking={handleTrackBooking}
        matchingStatus={successModal.data?.matchingStatus}
        contactPhone={successModal.data?.contactPhone}
        contactPhone_country_code={successModal.data?.contactPhone_country_code}
        paymentStatus={successModal.data?.paymentStatus}
        paymentId={successModal.data?.paymentId}
        message={successModal.data?.message}
      />

      {/* Validation Error Modal */}
      <ErrorModal
        isOpen={validationErrorModal.isOpen}
        onClose={() => {
          setValidationErrorModal({
            isOpen: false,
            title: "Validation Error",
            message: "",
          });
        }}
        title={validationErrorModal.title}
        message={validationErrorModal.message}
      />

      <OpenToOtherFleetsModal
        isOpen={openOthersModal.isOpen}
        preferredFleetName={openOthersModal.preferredFleetName}
        onConfirm={handleConfirmOpenToOthers}
        onCancel={handleCancelOpenToOthers}
      />

      {/* Payment Initiation Modal - Step 1 (Hospital Users Only) */}
      {!isFleetUser() && (
        <PaymentInitiationModal
          isOpen={paymentModal.isOpen}
          onClose={() => {
            setPaymentModal({
              isOpen: false,
              bookingId: null,
              bookingData: null,
              estimatedFare: null,
              patientName: null,
              pickupAddress: null,
              destinationAddress: null,
              ambulanceType: null,
            });
          }}
          bookingId={paymentModal.bookingId}
          bookingData={paymentModal.bookingData}
          estimatedFare={paymentModal.estimatedFare}
          patientName={paymentModal.patientName}
          pickupAddress={paymentModal.pickupAddress}
          destinationAddress={paymentModal.destinationAddress}
          ambulanceType={paymentModal.ambulanceType}
          onPaymentSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
        />
      )}

      {/* Payment Order Creation Modal (Fleet Users Only) */}
      {isFleetUser() && (
        <PaymentOrderCreationModal
          isOpen={paymentOrderModal.isOpen}
          onClose={() => {
            setPaymentOrderModal({
              isOpen: false,
              bookingId: null,
              bookingData: null,
              estimatedFare: null,
            });
          }}
          bookingId={paymentOrderModal.bookingId}
          bookingData={paymentOrderModal.bookingData}
          dashboardType="fleet"
          onOrderCreated={(orderData) => {
            console.log('✅ [AmbulanceInstantBooking] Payment order created, opening verification:', orderData);
            // Close order creation modal
            setPaymentOrderModal({
              isOpen: false,
              bookingId: null,
              bookingData: null,
              estimatedFare: null,
            });
            // Open verification modal with payment details
            setVerificationModal({
              isOpen: true,
              orderId: orderData.orderId,
              paymentId: orderData.paymentId,
              signature: orderData.signature,
              bookingId: paymentOrderModal.bookingId,
            });
          }}
          onError={handlePaymentError}
        />
      )}

      {/* Payment Verification Modal - Step 2 */}
      <PaymentVerificationModal
        isOpen={verificationModal.isOpen}
        onClose={() => {
          setVerificationModal({
            isOpen: false,
            orderId: null,
            paymentId: null,
            signature: null,
            bookingId: null,
          });
        }}
        orderId={verificationModal.orderId}
        paymentId={verificationModal.paymentId}
        signature={verificationModal.signature}
        bookingId={verificationModal.bookingId}
        autoVerify={true}
        dashboardType={isFleetUser() ? 'fleet' : 'hospital'}
        onVerificationSuccess={handleVerificationSuccess}
        onVerificationFailure={handleVerificationFailure}
      />

      {/* Notification Container for backend error messages */}
      <NotificationContainer notification={notification} />
    </div>
  );
};

export default AmbulanceInstantBooking;
